import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Megaphone, Send, Eye, Users, Plus } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  PageHeader,
  StatusBadge,
  useToast,
} from '@/components/ui'
import type { ColumnDef } from '@tanstack/react-table'

interface Campaign {
  id: string
  name: string
  senderUserId: string
  senderMailbox: string | null
  fieldSetJson: string[]
  tokenTtlDays: number
  status: 'draft' | 'ready' | 'sent' | 'archived'
  createdBy: string
  createdAt: string
}

interface Recipient {
  id: string
  bpId: string
  bpName: string
  bpEmail: string | null
  status: 'pending' | 'sent' | 'opened' | 'submitted' | 'error'
  sentAt: string | null
  openedAt: string | null
  submittedAt: string | null
}

const CAMPAIGN_STATUS_MAP: Record<string, string> = {
  draft: 'pending', ready: 'active', sent: 'success', archived: 'inactive',
}
const CAMPAIGN_STATUS_LABEL: Record<string, string> = {
  draft: 'Entwurf', ready: 'Bereit', sent: 'Versendet', archived: 'Archiviert',
}

const AVAILABLE_FIELDS = [
  { value: 'name', label: 'Firmenname' },
  { value: 'street', label: 'Straße' },
  { value: 'zipCode', label: 'PLZ' },
  { value: 'city', label: 'Stadt' },
  { value: 'country', label: 'Land' },
  { value: 'contactPhone', label: 'Telefon' },
  { value: 'website', label: 'Website' },
  { value: 'industryName', label: 'Branche' },
]

const RECIPIENT_STATUS_MAP: Record<string, string> = {
  pending: 'pending', sent: 'info', opened: 'warning', submitted: 'success', error: 'danger',
}
const RECIPIENT_STATUS_LABEL: Record<string, string> = {
  pending: 'Ausstehend', sent: 'Gesendet', opened: 'Geöffnet', submitted: 'Ausgefüllt', error: 'Fehler',
}

export default function GpCampaignsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null)
  const [form, setForm] = useState({ name: '', fieldSet: [] as string[], tokenTtlDays: 30 })
  const [recipientFilter, setRecipientFilter] = useState<string>('incomplete-quality')

  const { data: campaigns, isLoading, isError, error } = useQuery<Campaign[]>({
    queryKey: ['gp-campaigns'],
    queryFn: () => apiClient.get('/gp-campaigns').then((r) => r.data),
  })

  const { data: recipientPreview } = useQuery<{ data: Array<{ bpId: string; bpName: string; bpEmail: string | null }>; total: number }>({
    queryKey: ['gp-campaign-recipient-preview', selectedCampaign?.id, recipientFilter],
    queryFn: () => apiClient.get(`/gp-campaigns/${selectedCampaign!.id}/recipients/preview?filter=${recipientFilter}`).then((r) => r.data),
    enabled: !!selectedCampaign,
  })

  const { data: recipients } = useQuery<Recipient[]>({
    queryKey: ['gp-campaign-recipients', selectedCampaign?.id],
    queryFn: () => apiClient.get(`/gp-campaigns/${selectedCampaign!.id}/recipients`).then((r) => r.data),
    enabled: !!selectedCampaign && selectedCampaign.status !== 'draft',
  })

  const createMutation = useMutation({
    mutationFn: () => apiClient.post('/gp-campaigns', form),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Kampagne angelegt' })
      setCreateOpen(false)
      setForm({ name: '', fieldSet: [], tokenTtlDays: 30 })
      queryClient.invalidateQueries({ queryKey: ['gp-campaigns'] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  const addRecipientsMutation = useMutation({
    mutationFn: () => apiClient.post(`/gp-campaigns/${selectedCampaign!.id}/recipients`, { filter: recipientFilter }),
    onSuccess: (res) => {
      const data = res.data as { added?: number }
      toast({ tone: 'success', title: `${data.added ?? 0} Empfänger hinzugefügt` })
      queryClient.invalidateQueries({ queryKey: ['gp-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['gp-campaign-recipients', selectedCampaign?.id] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  const previewMutation = useMutation({
    mutationFn: () => apiClient.post(`/gp-campaigns/${selectedCampaign!.id}/preview`, {}),
    onSuccess: (res) => {
      const data = res.data as { sentTo?: string }
      toast({ tone: 'success', title: `Vorschau-Mail gesendet an ${data.sentTo}` })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  const sendMutation = useMutation({
    mutationFn: () => apiClient.post(`/gp-campaigns/${selectedCampaign!.id}/send`, {}),
    onSuccess: (res) => {
      const data = res.data as { sent?: number; errors?: string[] }
      toast({ tone: 'success', title: `${data.sent ?? 0} Mails versendet` })
      queryClient.invalidateQueries({ queryKey: ['gp-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['gp-campaign-recipients', selectedCampaign?.id] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  const campaignCols: ColumnDef<Campaign>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <button className="text-brand-600 hover:underline text-body font-medium" onClick={() => setSelectedCampaign(row.original)}>
          {row.original.name}
        </button>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge status={CAMPAIGN_STATUS_MAP[row.original.status] ?? 'default'} label={CAMPAIGN_STATUS_LABEL[row.original.status] ?? row.original.status} />
      ),
    },
    {
      accessorKey: 'fieldSetJson',
      header: 'Felder',
      cell: ({ row }) => row.original.fieldSetJson.length,
    },
    {
      accessorKey: 'tokenTtlDays',
      header: 'Token-TTL',
      cell: ({ row }) => `${row.original.tokenTtlDays} Tage`,
    },
    {
      accessorKey: 'createdAt',
      header: 'Erstellt',
      cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString('de-DE'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button variant="ghost" size="xs" onClick={() => setSelectedCampaign(row.original)}>
          Öffnen
        </Button>
      ),
    },
  ]

  const recipientCols: ColumnDef<Recipient>[] = [
    { accessorKey: 'bpName', header: 'Geschäftspartner' },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusBadge status={RECIPIENT_STATUS_MAP[row.original.status] ?? 'default'} label={RECIPIENT_STATUS_LABEL[row.original.status] ?? row.original.status} />
      ),
    },
    { accessorKey: 'sentAt', header: 'Gesendet', cell: ({ row }) => row.original.sentAt ? new Date(row.original.sentAt).toLocaleString('de-DE') : '—' },
    { accessorKey: 'openedAt', header: 'Geöffnet', cell: ({ row }) => row.original.openedAt ? new Date(row.original.openedAt).toLocaleString('de-DE') : '—' },
    { accessorKey: 'submittedAt', header: 'Ausgefüllt', cell: ({ row }) => row.original.submittedAt ? new Date(row.original.submittedAt).toLocaleString('de-DE') : '—' },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="GP-Kampagnen"
        description="Stammdaten-Aktualisierungsanfragen per E-Mail versenden"
        breadcrumbs={[{ label: 'Geschäftspartner', to: '/gp' }, { label: 'Kampagnen' }]}
        actions={
          <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Neue Kampagne
          </Button>
        }
      />

      {/* Kampagnen-Liste */}
      {isLoading && <LoadingState label="Lade Kampagnen …" />}
      {isError && <ErrorState title="Fehler" description={(error as Error)?.message} />}
      {!isLoading && !isError && (
        <Card>
          <Card.Body className="p-0">
            <DataTable
              columns={campaignCols}
              data={campaigns ?? []}
              pagination={{ pageSize: 20 }}
              emptyState={<EmptyState title="Keine Kampagnen" description="Erstelle deine erste Kampagne." />}
            />
          </Card.Body>
        </Card>
      )}

      {/* Kampagnen-Detail-Drawer */}
      {selectedCampaign && (
        <Modal
          open={!!selectedCampaign}
          onOpenChange={(v) => { if (!v) setSelectedCampaign(null) }}
          title={selectedCampaign.name}
          size="lg"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <StatusBadge status={CAMPAIGN_STATUS_MAP[selectedCampaign.status] ?? 'default'} label={CAMPAIGN_STATUS_LABEL[selectedCampaign.status] ?? selectedCampaign.status} />
              <span className="text-small text-gray-500">
                Felder: {selectedCampaign.fieldSetJson.join(', ')} · TTL: {selectedCampaign.tokenTtlDays}d
              </span>
            </div>

            {/* Empfaenger-Auswahl */}
            {selectedCampaign.status === 'draft' && (
              <div className="space-y-2">
                <h4 className="text-h4 text-gray-900">Empfänger-Filter</h4>
                <select
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-small"
                  value={recipientFilter}
                  onChange={(e) => setRecipientFilter(e.target.value)}
                >
                  <option value="incomplete-quality">Unvollständige Datenqualität</option>
                  <option value="all-active">Alle aktiven GP</option>
                  <option value="missing-email">Ohne E-Mail-Adresse</option>
                  <option value="all">Alle GP</option>
                </select>
                {recipientPreview && (
                  <p className="text-small text-gray-600">
                    <Users className="w-3.5 h-3.5 inline mr-1" />
                    {recipientPreview.total} Empfänger würden ausgewählt
                  </p>
                )}
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" onClick={() => previewMutation.mutate()} disabled={previewMutation.isPending}>
                    <Eye className="w-4 h-4 mr-2" />
                    Vorschau-Mail an mich
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => addRecipientsMutation.mutate()} disabled={addRecipientsMutation.isPending}>
                    Empfänger hinzufügen & bereitstellen
                  </Button>
                </div>
              </div>
            )}

            {/* Versand */}
            {selectedCampaign.status === 'ready' && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => previewMutation.mutate()} disabled={previewMutation.isPending}>
                    <Eye className="w-4 h-4 mr-2" />
                    Vorschau-Mail an mich
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending}>
                    <Send className="w-4 h-4 mr-2" />
                    {sendMutation.isPending ? 'Wird versendet …' : 'Kampagne versenden'}
                  </Button>
                </div>
              </div>
            )}

            {/* Empfaenger-Tabelle (nach Versand) */}
            {(selectedCampaign.status === 'sent' || selectedCampaign.status === 'ready') && recipients && (
              <div>
                <h4 className="text-h4 text-gray-900 mb-2">Empfänger-Status</h4>
                <DataTable
                  columns={recipientCols}
                  data={recipients}
                  pagination={{ pageSize: 20 }}
                  emptyState={<EmptyState title="Keine Empfänger" description="Noch keine Empfänger hinzugefügt." />}
                />
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Kampagne erstellen */}
      <Modal
        open={createOpen}
        onOpenChange={(v) => { if (!v) setCreateOpen(false) }}
        title="Neue Kampagne erstellen"
      >
        <div className="space-y-4">
          <div>
            <label className="text-small font-medium text-gray-700 block mb-1">Kampagnen-Name</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-small"
              placeholder="z.B. Stammdaten-Pflege Q2 2026"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-small font-medium text-gray-700 block mb-2">Welche Felder soll der GP ausfüllen?</label>
            <div className="grid grid-cols-2 gap-2">
              {AVAILABLE_FIELDS.map((f) => (
                <label key={f.value} className="flex items-center gap-2 text-small">
                  <input
                    type="checkbox"
                    checked={form.fieldSet.includes(f.value)}
                    onChange={(e) => setForm((p) => ({
                      ...p,
                      fieldSet: e.target.checked
                        ? [...p.fieldSet, f.value]
                        : p.fieldSet.filter((x) => x !== f.value),
                    }))}
                  />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-small font-medium text-gray-700 block mb-1">Token-Laufzeit (Tage)</label>
            <input
              type="number"
              className="w-32 border border-gray-300 rounded-md px-3 py-1.5 text-small"
              value={form.tokenTtlDays}
              min={1}
              max={365}
              onChange={(e) => setForm((p) => ({ ...p, tokenTtlDays: Number(e.target.value) }))}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setCreateOpen(false)}>Abbrechen</Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => createMutation.mutate()}
              disabled={!form.name.trim() || form.fieldSet.length === 0 || createMutation.isPending}
            >
              <Megaphone className="w-4 h-4 mr-2" />
              Kampagne anlegen
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
